/**
 * Audio recording types and interfaces for MediaRecorder API
 * Based on browser compatibility research and best practices
 */

/**
 * Recording states following MediaRecorder lifecycle
 */
export enum RecordingState {
  IDLE = 'idle',
  REQUESTING = 'requesting',
  RECORDING = 'recording',
  PAUSED = 'paused',
  STOPPED = 'stopped',
  ERROR = 'error'
}

/**
 * Audio format configuration based on browser capabilities
 */
export interface AudioFormatConfig {
  mimeType: string;
  codec: 'opus' | 'aac' | 'webm' | 'mp4';
  audioBitsPerSecond?: number;
  fileExtension: string;
  quality: 'optimized' | 'standard' | 'high';
  estimatedBitrate?: number;
}

/**
 * MediaRecorder configuration options
 */
export interface MediaRecorderConfig {
  mimeType: string;
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  bitsPerSecond?: number;
}

/**
 * Audio recording constraints for getUserMedia
 */
export interface AudioConstraints {
  audio: {
    channelCount?: number;
    sampleRate?: number;
    echoCancellation?: boolean;
    noiseSuppression?: boolean;
    autoGainControl?: boolean;
  };
}

/**
 * Recorded audio data with metadata
 */
export interface AudioRecordingData {
  blob: Blob;
  duration: number;
  mimeType: string;
  size: number;
  format: AudioFormatConfig;
  waveformData?: number[];
}

/**
 * Audio recorder state
 */
export interface AudioRecorderState {
  recordingState: RecordingState;
  duration: number;
  audioBlob: Blob | null;
  isPaused: boolean;
  error: AudioRecorderError | null;
  isSupported: boolean;
  currentFormat: AudioFormatConfig | null;
}

/**
 * Error types for audio recording
 */
export enum AudioRecorderErrorType {
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  DEVICE_NOT_FOUND = 'DEVICE_NOT_FOUND',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  RECORDING_FAILED = 'RECORDING_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Audio recorder error with contextual information
 */
export interface AudioRecorderError {
  type: AudioRecorderErrorType;
  message: string;
  originalError?: Error;
  recoverable: boolean;
}

/**
 * Browser compatibility information
 */
export interface BrowserCompatibility {
  isMediaRecorderSupported: boolean;
  isGetUserMediaSupported: boolean;
  supportedMimeTypes: string[];
  recommendedFormat: AudioFormatConfig | null;
  isMobile: boolean;
  isIOS: boolean;
  isSafari: boolean;
}

/**
 * Permission states for microphone access
 */
export enum PermissionState {
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
  UNKNOWN = 'unknown'
}

/**
 * Audio recorder hooks return type
 */
export interface UseAudioRecorderReturn {
  // State
  state: AudioRecorderState;

  // Controls
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<AudioRecordingData | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;

  // Utilities
  checkPermission: () => Promise<PermissionState>;
  requestPermission: () => Promise<boolean>;
  cleanup: () => void;
}

/**
 * Waveform visualization data
 */
export interface WaveformData {
  peaks: number[];
  duration: number;
  sampleRate: number;
}
